import React, { useEffect, useRef, useState, forwardRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Maximize2, ZoomIn, ZoomOut, Download } from "lucide-react";
import HTMLFlipBook from "react-pageflip";
import * as pdfjsLib from "pdfjs-dist";
import api, { API } from "../lib/api";
import { useI18n } from "../context/I18nContext";

// PDF.js worker (CDN — matches the installed version)
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Resolve a PDF URL for loading via PDF.js.
//  - absolute (http/https) → route through backend pdf-proxy to bypass CORS
//  - /api/*  → load directly from the backend, same-origin (no proxy needed)
//  - other /*  → load directly from the frontend origin
const proxied = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return `${API}/pdf-proxy?url=${encodeURIComponent(url)}`;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  if (url.startsWith("/")) {
    if (typeof window !== "undefined") return `${window.location.origin}${url}`;
    return url;
  }
  return url;
};

const Page = forwardRef(({ src, number, total }, ref) => (
  <div ref={ref} className="flipbook-page" data-density="hard">
    <div className="flipbook-page-inner">
      {src ? (
        <img src={src} alt={`Page ${number}`} draggable="false" />
      ) : (
        <div className="flipbook-page-loading">…</div>
      )}
      <div className="flipbook-pageno">{number} / {total}</div>
    </div>
  </div>
));
Page.displayName = "FlipPage";

export default function Flipbook() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, tr } = useI18n();
  const [mag, setMag] = useState(null);
  const [pages, setPages] = useState([]);   // array of dataURL strings
  const [progress, setProgress] = useState({ loaded: 0, total: 0 });
  const [zoom, setZoom] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const wrapRef = useRef(null);
  const bookRef = useRef(null);

  // Auto-fit zoom to viewport on load & resize
  useEffect(() => {
    const fit = () => {
      const availableH = window.innerHeight - 140; // room for header + padding
      const availableW = window.innerWidth - 80;
      const bookH = 720;
      const bookW = 520 * 2; // spread = 2 pages side by side
      const scale = Math.min(availableH / bookH, availableW / bookW, 1.4);
      setZoom(Math.max(0.6, +scale.toFixed(2)));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  // Fetch magazine metadata
  useEffect(() => {
    api.get(`/magazines/${id}`).then((r) => setMag(r.data)).catch(() => setMag(null));
  }, [id]);

  // Render PDF pages → images
  useEffect(() => {
    if (!mag?.pdf_url) return;
    let cancelled = false;
    const renderAll = async () => {
      try {
        const url = proxied(mag.pdf_url);
        const pdf = await pdfjsLib.getDocument({ url, withCredentials: false }).promise;
        if (cancelled) return;
        setProgress({ loaded: 0, total: pdf.numPages });
        const out = new Array(pdf.numPages).fill(null);
        // Render sequentially to keep memory under control
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.6 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext("2d");
          await page.render({ canvasContext: ctx, viewport }).promise;
          if (cancelled) return;
          out[i - 1] = canvas.toDataURL("image/jpeg", 0.85);
          setPages([...out]);
          setProgress({ loaded: i, total: pdf.numPages });
        }
      } catch (e) {
        console.error("PDF render error", e);
        setError(e?.message || "PDF load failed");
      }
    };
    renderAll();
    return () => { cancelled = true; };
  }, [mag]);

  const goFullscreen = () => {
    const el = wrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };

  const flipNext = () => bookRef.current?.pageFlip()?.flipNext();
  const flipPrev = () => bookRef.current?.pageFlip()?.flipPrev();
  const onFlip = (e) => setCurrentPage((e.data || 0) + 1);

  if (!mag) {
    return (
      <div className="flipbook-backdrop items-center justify-center">
        <p className="text-[var(--gai-white)]">{t("common.loading")}</p>
      </div>
    );
  }

  const total = pages.length || progress.total || 0;
  const ready = total > 0 && pages.every(Boolean);

  return (
    <div className="flipbook-backdrop" data-testid="page-flipbook" ref={wrapRef}>
      <header className="flex items-center justify-between px-6 py-4 border-b border-[var(--gai-grey-300)]">
        <div className="flex items-center gap-4">
          <div>
            <div className="overline text-[0.66rem]">{mag.edition}</div>
            <h2 className="serif text-xl md:text-2xl text-[var(--gai-white)] leading-tight">{tr(mag.title)}</h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {mag.pdf_url && (
            <a href={mag.pdf_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${mag.pdf_url}` : mag.pdf_url} target="_blank" rel="noreferrer" className="gai-btn-ghost" data-testid="flipbook-download">
              <Download size={14} /> PDF
            </a>
          )}
          <button className="gai-btn-ghost" onClick={() => setZoom((z) => Math.max(0.6, z - 0.1))} data-testid="flipbook-zoom-out">
            <ZoomOut size={14} />
          </button>
          <span className="text-xs text-[rgba(255,255,255,0.7)] tabular-nums">{Math.round(zoom * 100)}%</span>
          <button className="gai-btn-ghost" onClick={() => setZoom((z) => Math.min(1.6, z + 0.1))} data-testid="flipbook-zoom-in">
            <ZoomIn size={14} />
          </button>
          <button className="gai-btn-ghost" onClick={goFullscreen} data-testid="flipbook-fullscreen">
            <Maximize2 size={14} />
          </button>
          <button className="gai-btn-ghost" onClick={() => navigate("/magazine")} data-testid="flipbook-close">
            <X size={16} /> {t("common.close")}
          </button>
        </div>
      </header>

      <div className="flipbook-stage relative">
        {!mag.pdf_url ? (
          <div className="text-center max-w-md p-10 border border-[var(--gai-grey-300)]" data-testid="flipbook-nopdf">
            <div className="overline mb-4">{mag.edition}</div>
            <h3 className="serif text-3xl text-[var(--gai-white)]">{tr(mag.title)}</h3>
            <p className="mt-4 text-[rgba(255,255,255,0.75)]">{t("magazine.noPdf")}</p>
          </div>
        ) : error ? (
          <div className="text-center max-w-md p-10 border border-red-400/40" data-testid="flipbook-error">
            <p className="text-red-300">Erreur de chargement du PDF.</p>
            <p className="text-xs mt-2 text-[rgba(255,255,255,0.55)]">{String(error)}</p>
          </div>
        ) : !ready ? (
          <div className="text-center" data-testid="flipbook-loading">
            <div className="overline mb-3">{t("common.loading")}</div>
            <div className="w-72 h-[2px] bg-[var(--gai-grey-200)] relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-[var(--gai-red)] transition-all duration-200"
                style={{ width: `${progress.total ? (progress.loaded / progress.total) * 100 : 8}%` }}
              />
            </div>
            <p className="mt-3 text-sm text-[rgba(255,255,255,0.7)]">
              {progress.loaded} / {progress.total || "…"} pages
            </p>
          </div>
        ) : (
          <div
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: "center center",
              transition: "transform 250ms ease",
              filter: "drop-shadow(0 30px 80px rgba(0,0,0,0.6))",
            }}
          >
            <HTMLFlipBook
              ref={bookRef}
              width={520}
              height={720}
              size="stretch"
              minWidth={300}
              maxWidth={900}
              minHeight={420}
              maxHeight={1100}
              maxShadowOpacity={0.5}
              showCover
              mobileScrollSupport
              onFlip={onFlip}
              className="gai-flipbook"
              data-testid="flipbook-book"
            >
              {pages.map((src, idx) => (
                <Page key={idx} src={src} number={idx + 1} total={total} />
              ))}
            </HTMLFlipBook>
          </div>
        )}

        {/* Side controls */}
        {ready && (
          <>
            <button
              onClick={flipPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 border border-[var(--gai-grey-300)] text-[var(--gai-red)] hover:bg-[var(--gai-grey-200)]"
              data-testid="flipbook-prev"
              aria-label="Page précédente"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              onClick={flipNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 border border-[var(--gai-grey-300)] text-[var(--gai-red)] hover:bg-[var(--gai-grey-200)]"
              data-testid="flipbook-next"
              aria-label="Page suivante"
            >
              <ChevronRight size={22} />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-1 text-[0.7rem] tracking-[0.2em] uppercase text-[var(--gai-red)] border border-[var(--gai-grey-300)] bg-[rgba(5,16,36,0.7)] backdrop-blur-sm">
              <span data-testid="flipbook-page-indicator">Page {currentPage} / {total}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
